import React, { useState, useEffect } from "react";
import {
    makeStyles,
    Card,
    CardContent,
    CardHeader,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Box,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Backdrop
} from "@material-ui/core";
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import ButtonWithSpinner from "../ButtonWithSpinner";
import { i18n } from "../../translate/i18n";
import toastUtils from "../../utils/toastUtils";

// Helper function to ensure i18n returns a string
const t = (key, fallback = '') => {
    const result = i18n.t(key);
    return typeof result === 'string' ? result : (fallback || key.split('.').pop());
};

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%'
    },
    formCard: {
        borderRadius: theme.spacing(2),
        boxShadow: 'none',
        border: 'none',
        position: 'relative'
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        borderRadius: theme.spacing(2)
    },
    fullWidth: {
        width: '100%'
    },
    formSection: {
        marginBottom: theme.spacing(3)
    },
    sectionTitle: {
        fontSize: '1.1rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(2),
        paddingBottom: theme.spacing(1),
        borderBottom: `1px solid ${theme.palette.divider}`
    },
    fieldGroup: {
        marginBottom: theme.spacing(2)
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(2),
        marginTop: theme.spacing(3),
        paddingTop: theme.spacing(2),
        borderTop: `1px solid ${theme.palette.divider}`,
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            gap: theme.spacing(1)
        }
    },
    button: {
        minWidth: 120,
        [theme.breakpoints.down('sm')]: {
            minWidth: 'auto',
            width: '100%'
        }
    },
    cancelButton: {
        minWidth: 120,
        [theme.breakpoints.down('sm')]: {
            minWidth: 'auto',
            width: '100%'
        }
    },
    deleteButton: {
        minWidth: 120,
        [theme.breakpoints.down('sm')]: {
            minWidth: 'auto',
            width: '100%'
        }
    }
}));

// Validation schema
const validationSchema = Yup.object().shape({
    name: Yup.string()
        .required('Nome do plano é obrigatório')
        .min(2, 'Nome deve ter pelo menos 2 caracteres')
        .max(100, 'Nome deve ter no máximo 100 caracteres'),
    users: Yup.number()
        .required('Número de usuários é obrigatório')
        .min(1, 'Deve ter pelo menos 1 usuário')
        .max(10000, 'Número de usuários não pode exceder 10.000'),
    connections: Yup.number()
        .required('Número de conexões é obrigatório')
        .min(1, 'Deve ter pelo menos 1 conexão')
        .max(1000, 'Número de conexões não pode exceder 1.000'),
    queues: Yup.number()
        .required('Número de filas é obrigatório')
        .min(1, 'Deve ter pelo menos 1 fila')
        .max(100, 'Número de filas não pode exceder 100'),
    amount: Yup.string()
        .required('Valor é obrigatório')
        .matches(/^\d+([.,]\d{1,2})?$/, 'Formato de valor inválido (ex: 99,99)'),
    trialDays: Yup.number()
        .when('trial', {
            is: true,
            then: Yup.number()
                .required('Dias de trial são obrigatórios quando trial está ativo')
                .min(1, 'Deve ter pelo menos 1 dia de trial')
                .max(365, 'Período de trial não pode exceder 365 dias')
        }),
    recurrence: Yup.string()
        .required('Recorrência é obrigatória')
        .oneOf(['monthly', 'yearly'], 'Recorrência deve ser mensal ou anual')
});

export default function PlanManagerForm(props) {
    const { 
        onSubmit, 
        onDelete, 
        onCancel, 
        initialValue, 
        loading,
        mode = 'create', // 'create' or 'edit'
        hideActions = false // Hide action buttons when used in modal
    } = props;
    
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [record, setRecord] = useState({
        name: '',
        users: 0,
        connections: 0,
        queues: 0,
        amount: 0,
        useWhatsapp: true,
        useFacebook: true,
        useInstagram: true,
        useCampaigns: true,
        useSchedules: true,
        useInternalChat: true,
        useExternalApi: true,
        useKanban: true,
        useOpenAi: true,
        useIntegrations: true,
        useIASDR: true,
        isPublic: true,
        useWhatsappOfficial: false,
        useGroups: false,
        useLandingPages: false,
        useWebChat: false,
        useWhatsappCatalog: false,
        useAssistants: false,
        useAsterisk: false,
        onlyApiMessage: false,
        trial: false,
        trialDays: 3,
        recurrence: 'monthly'
    });

    useEffect(() => {
        if (initialValue) {
            setRecord(initialValue);
        }
    }, [initialValue]);

    const handleSubmit = async (values, { setSubmitting, resetForm, setFieldError }) => {
        try {
            // Format amount value before submission
            const formattedValues = {
                ...values,
                amount: typeof values.amount === 'string' 
                    ? parseFloat(values.amount.replace(',', '.')) 
                    : values.amount
            };
            
            await onSubmit(formattedValues);
            
            if (mode === 'create') {
                resetForm();
            }
        } catch (error) {
            console.error('Form submission error:', error);
            
            // Handle specific validation errors from backend
            if (error.response?.data?.errors) {
                const backendErrors = error.response.data.errors;
                Object.keys(backendErrors).forEach(field => {
                    setFieldError(field, backendErrors[field]);
                });
            } else {
                // Generic error handling
                toastUtils.error(
                    error.response?.data?.message || 'Erro ao salvar o plano. Tente novamente.',
                    'Erro no Formulário'
                );
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        // Reset form to initial state
        setRecord({
            name: '',
            users: 0,
            connections: 0,
            queues: 0,
            amount: 0,
            useWhatsapp: true,
            useFacebook: true,
            useInstagram: true,
            useCampaigns: true,
            useSchedules: true,
            useInternalChat: true,
            useExternalApi: true,
            useKanban: true,
            useOpenAi: true,
            useIntegrations: true,
            useIASDR: true,
            isPublic: true,
            useWhatsappOfficial: false,
            useGroups: false,
            useLandingPages: false,
            useWebChat: false,
            useWhatsappCatalog: false,
            useAssistants: false,
            useAsterisk: false,
            onlyApiMessage: false,
            trial: false,
            trialDays: 3,
            recurrence: 'monthly'
        });
        
        if (onCancel) {
            onCancel();
        }
    };

    const handleDelete = () => {
        if (onDelete && record.id) {
            onDelete(record);
        }
    };

    return (
        <div className={classes.root}>
            <Card className={classes.formCard} elevation={0}>
                {loading && (
                    <div className={classes.loadingOverlay}>
                        <CircularProgress size={40} />
                    </div>
                )}
                {!hideActions && (
                    <CardHeader 
                        title={mode === 'edit' ? "Editando Plano" : "Criar Novo Plano"}
                        subheader="Configure os recursos e limites do plano"
                        titleTypographyProps={{ variant: "h6", fontWeight: 600 }}
                    />
                )}
                <CardContent>
                    <Formik
                        enableReinitialize
                        initialValues={record}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ values, errors, touched, isSubmitting }) => (
                            <Form className={classes.fullWidth} id="plan-form">
                                {/* Basic Information Section */}
                                <div className={classes.formSection}>
                                    <div className={classes.sectionTitle}>
                                        Informações Básicas
                                    </div>
                                    <Grid container spacing={2} className={classes.fieldGroup}>
                                        {/* Plan Name */}
                                        <Grid item xs={12} sm={6}>
                                            <Field
                                                as={TextField}
                                                label={t("plans.form.name", "Nome")}
                                                name="name"
                                                variant="outlined"
                                                fullWidth
                                                margin="dense"
                                                error={touched.name && !!errors.name}
                                                helperText={touched.name && errors.name}
                                            />
                                        </Grid>

                                        {/* Amount */}
                                        <Grid item xs={12} sm={6}>
                                            <Field
                                                as={TextField}
                                                label="Valor"
                                                name="amount"
                                                variant="outlined"
                                                fullWidth
                                                margin="dense"
                                                type="text"
                                                error={touched.amount && !!errors.amount}
                                                helperText={touched.amount && errors.amount}
                                            />
                                        </Grid>

                                        {/* Public/Private */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>{i18n.t("plans.form.public")}</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label={i18n.t("plans.form.public")}
                                                    name="isPublic"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>Sim</MenuItem>
                                                    <MenuItem value={false}>Não</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* Trial */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>Trial</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label="Trial"
                                                    name="trial"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>Sim</MenuItem>
                                                    <MenuItem value={false}>Não</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* Trial Days */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field
                                                as={TextField}
                                                label={i18n.t("plans.form.trialDays")}
                                                name="trialDays"
                                                variant="outlined"
                                                fullWidth
                                                margin="dense"
                                                type="number"
                                                inputProps={{ min: 1, max: 365 }}
                                                disabled={!values.trial}
                                                error={touched.trialDays && !!errors.trialDays}
                                                helperText={touched.trialDays && errors.trialDays}
                                            />
                                        </Grid>

                                        {/* Recurrence */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth error={touched.recurrence && !!errors.recurrence}>
                                                <InputLabel>Recorrência</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label="Recorrência"
                                                    name="recurrence"
                                                    margin="dense"
                                                >
                                                    <MenuItem value="monthly">Mensal</MenuItem>
                                                    <MenuItem value="yearly">Anual</MenuItem>
                                                </Field>
                                                {touched.recurrence && errors.recurrence && (
                                                    <div style={{ color: '#f44336', fontSize: '0.75rem', marginTop: '3px', marginLeft: '14px' }}>
                                                        {errors.recurrence}
                                                    </div>
                                                )}
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </div>

                                {/* Limits Section */}
                                <div className={classes.formSection}>
                                    <div className={classes.sectionTitle}>
                                        Limites do Plano
                                    </div>
                                    <Grid container spacing={2} className={classes.fieldGroup}>
                                        {/* Users */}
                                        <Grid item xs={12} sm={4}>
                                            <Field
                                                as={TextField}
                                                label={i18n.t("plans.form.users")}
                                                name="users"
                                                variant="outlined"
                                                fullWidth
                                                margin="dense"
                                                type="number"
                                                error={touched.users && !!errors.users}
                                                helperText={touched.users && errors.users}
                                            />
                                        </Grid>

                                        {/* Connections */}
                                        <Grid item xs={12} sm={4}>
                                            <Field
                                                as={TextField}
                                                label={i18n.t("plans.form.connections")}
                                                name="connections"
                                                variant="outlined"
                                                fullWidth
                                                margin="dense"
                                                type="number"
                                                error={touched.connections && !!errors.connections}
                                                helperText={touched.connections && errors.connections}
                                            />
                                        </Grid>

                                        {/* Queues */}
                                        <Grid item xs={12} sm={4}>
                                            <Field
                                                as={TextField}
                                                label="Filas"
                                                name="queues"
                                                variant="outlined"
                                                fullWidth
                                                margin="dense"
                                                type="number"
                                                error={touched.queues && !!errors.queues}
                                                helperText={touched.queues && errors.queues}
                                            />
                                        </Grid>
                                    </Grid>
                                </div>

                                {/* Communication Channels Section */}
                                <div className={classes.formSection}>
                                    <div className={classes.sectionTitle}>
                                        Canais de Comunicação
                                    </div>
                                    <Grid container spacing={2} className={classes.fieldGroup}>
                                        {/* WhatsApp */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>Whatsapp</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label="Whatsapp"
                                                    name="useWhatsapp"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* WhatsApp Official */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>Whatsapp Oficial</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label="Whatsapp Oficial"
                                                    name="useWhatsappOfficial"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* Facebook */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>Facebook</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label="Facebook"
                                                    name="useFacebook"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* Instagram */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>Instagram</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label="Instagram"
                                                    name="useInstagram"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* WebChat */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>WebChat</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label="WebChat"
                                                    name="useWebChat"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </div>

                                {/* Advanced Features Section */}
                                <div className={classes.formSection}>
                                    <div className={classes.sectionTitle}>
                                        Recursos Avançados
                                    </div>
                                    <Grid container spacing={2} className={classes.fieldGroup}>
                                        {/* Campaigns */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>{i18n.t("plans.form.campaigns")}</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label={i18n.t("plans.form.campaigns")}
                                                    name="useCampaigns"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* Schedules */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>{i18n.t("plans.form.schedules")}</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label={i18n.t("plans.form.schedules")}
                                                    name="useSchedules"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* Internal Chat */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>Chat Interno</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label="Chat Interno"
                                                    name="useInternalChat"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* External API */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>API Externa</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label="API Externa"
                                                    name="useExternalApi"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* Kanban */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>Kanban</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label="Kanban"
                                                    name="useKanban"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* OpenAI */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>Talk.Ai</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label="Talk.Ai"
                                                    name="useOpenAi"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* Integrations */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>Integrações</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label="Integrações"
                                                    name="useIntegrations"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* IA SDR */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>IA SDR</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label="IA SDR"
                                                    name="useIASDR"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* Groups */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>Grupos</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label="Grupos"
                                                    name="useGroups"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* Landing Pages */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>Landing Pages</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label="Landing Pages"
                                                    name="useLandingPages"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* WhatsApp Catalog */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>{i18n.t("plans.form.whatsappCatalog")}</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label={i18n.t("plans.form.whatsappCatalog")}
                                                    name="useWhatsappCatalog"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* Assistants */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>Assistentes</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label="Assistentes"
                                                    name="useAssistants"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* Asterisk VoIP */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>{i18n.t("asterisk.phone")}</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label={i18n.t("asterisk.phone")}
                                                    name="useAsterisk"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* Only API Messages */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel>Apenas API de Mensagens</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label="Apenas API de Mensagens"
                                                    name="onlyApiMessage"
                                                    margin="dense"
                                                >
                                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </div>

                                {/* Action Buttons - Only show when not in modal mode */}
                                {!props.hideActions && (
                                    <Box className={classes.buttonContainer}>
                                        <ButtonWithSpinner 
                                            className={classes.cancelButton}
                                            onClick={handleCancel} 
                                            variant="outlined"
                                            disabled={loading || isSubmitting}
                                        >
                                            {i18n.t("plans.form.clear")}
                                        </ButtonWithSpinner>
                                        
                                        {mode === 'edit' && record.id && (
                                            <ButtonWithSpinner 
                                                className={classes.deleteButton}
                                                loading={loading} 
                                                onClick={handleDelete} 
                                                variant="contained" 
                                                color="secondary"
                                                disabled={isSubmitting}
                                            >
                                                {i18n.t("plans.form.delete")}
                                            </ButtonWithSpinner>
                                        )}
                                        
                                        <ButtonWithSpinner 
                                            className={classes.button}
                                            loading={loading || isSubmitting} 
                                            type="submit" 
                                            variant="contained" 
                                            color="primary"
                                        >
                                            {i18n.t("plans.form.save")}
                                        </ButtonWithSpinner>
                                    </Box>
                                )}
                            </Form>
                        )}
                    </Formik>
                </CardContent>
            </Card>
        </div>
    );
}